(() => {
  const CALCULATOR_SELECTOR = '#formula-calculator-section';
  const SIZE_SELECTOR = '[data-novva-size-options]';
  const CUSTOM_ACTIVE_CLASS = 'novva-pc-custom-active';
  const PROPERTY_NAMES = {
    type: 'Ölçü türü',
    requestedWidth: 'İstenen En (cm)',
    requestedLength: 'İstenen Boy (cm)',
    billableWidth: 'Fiyatlandırılan En (cm)',
    billableLength: 'Fiyatlandırılan Boy (cm)',
    billableArea: 'Fiyatlandırılan Alan (m²)',
    unitPrice: 'm² fiyatı',
    calculatedTotal: 'Hesaplanan toplam',
  };

  let lastReadyQuote = null;
  let lastReadyComponent = null;
  let pendingResyncTimers = [];
  const priceLocks = new WeakMap();

  const formatMoney = (cents, currency) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency || 'TRY' }).format(cents / 100);

  const formatArea = (area) =>
    `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(area)} m²`;

  const formatPlainArea = (area) =>
    new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(area);

  const formatPlainMoney = (cents, currency) =>
    formatMoney(cents, currency).replace(/\s/g, ' ').trim();

  const quoteProperties = (quote) => {
    if (!quote || quote.status !== 'ready') return null;

    return {
      [PROPERTY_NAMES.type]: 'Özel ölçü',
      [PROPERTY_NAMES.requestedWidth]: String(quote.requestedWidthCm),
      [PROPERTY_NAMES.requestedLength]: String(quote.requestedLengthCm),
      [PROPERTY_NAMES.billableWidth]: String(quote.billableWidthCm),
      [PROPERTY_NAMES.billableLength]: String(quote.billableLengthCm),
      [PROPERTY_NAMES.billableArea]: formatArea(quote.billableAreaM2),
      [PROPERTY_NAMES.unitPrice]: formatMoney(quote.unitPriceCents, quote.currency),
      [PROPERTY_NAMES.calculatedTotal]: formatMoney(quote.totalPriceCents, quote.currency),
    };
  };

  const REDUNDANT_CART_PROPERTY_NAMES = new Set(
    [
      'Ölçü',
      'En',
      'En (cm)',
      'Boy',
      'Boy (cm)',
      'Toplam Fiyat',
      'Toplam',
      'Fiyatlandırılan Alan',
      'FiyatlandırılAlan',
      'Fiyatlandırılan Ölçü',
    ].map((name) => name.toLocaleLowerCase('tr-TR'))
  );

  const normalizePropertyName = (name) => String(name || '').trim().toLocaleLowerCase('tr-TR');

  const isRedundantCartProperty = (name) => {
    const propertyName = String(name || '').trim();
    return /^properties\[/i.test(propertyName) || REDUNDANT_CART_PROPERTY_NAMES.has(normalizePropertyName(propertyName));
  };

  const cleanCartProperties = (currentProperties, quote) => {
    const nextProperties = {};

    if (currentProperties && typeof currentProperties === 'object' && !Array.isArray(currentProperties)) {
      Object.entries(currentProperties).forEach(([name, value]) => {
        if (!isRedundantCartProperty(name)) nextProperties[name] = value;
      });
    }

    return { ...nextProperties, ...quoteProperties(quote) };
  };

  const appPropertyAliases = (quote) => {
    if (!quote || quote.status !== 'ready') return {};

    return {
      Ölçü: `${quote.requestedWidthCm} × ${quote.requestedLengthCm} cm`,
      'm² fiyatı': String(quote.unitPriceCents / 100),
      'Toplam Fiyat': `${quote.totalPriceCents / 100} TL`,
      Toplam: formatPlainMoney(quote.totalPriceCents, quote.currency),
      'Fiyatlandırılan Alan': `${formatPlainArea(quote.billableAreaM2)} m2`,
      FiyatlandırılAlan: `${formatPlainArea(quote.billableAreaM2)} m2`,
      'Fiyatlandırılan Ölçü': `${quote.billableWidthCm} × ${quote.billableLengthCm} cm`,
    };
  };

  const quoteCalculatorFields = (quote) => {
    if (!quote || quote.status !== 'ready') return {};

    return {
      width: String(quote.billableWidthCm),
      length: String(quote.billableLengthCm),
      unit_price: String(quote.unitPriceCents / 100),
      product_price: String(quote.unitPriceCents / 100),
      formula_0: 'width*length/10000',
      formula_1: 'formula_0*unit_price',
    };
  };

  const setNativeValue = (input, value) => {
    if (!input) return false;
    const nextValue = String(value);
    if (input.value === nextValue) return false;

    const prototype = Object.getPrototypeOf(input);
    const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
    if (descriptor?.set) {
      descriptor.set.call(input, nextValue);
    } else {
      input.value = nextValue;
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  const pulseCalculatorField = (input) => {
    if (!input) return;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const hideCalculatorField = (input) => {
    if (!input) return;

    const fieldWrapper =
      input.closest('.form-group') ||
      input.closest('.af-field') ||
      input.closest('.field') ||
      input.closest('tr') ||
      input.closest('div');

    if (fieldWrapper) {
      fieldWrapper.hidden = true;
      fieldWrapper.style.display = 'none';
    } else {
      input.hidden = true;
      input.style.display = 'none';
    }
  };

  const findCalculator = (component) => {
    const details = component?.closest('.product-details');
    return details?.querySelector(CALCULATOR_SELECTOR) || document.querySelector(CALCULATOR_SELECTOR);
  };

  const getCalculatorPriceInputs = (calculator) => ({
    productPriceInput: calculator?.querySelector('input[name="product_price"], #product_price'),
    unitPriceInput: calculator?.querySelector('input[name="unit_price"], #unit_price'),
    totalFormulaInput: calculator?.querySelector('input[name="formula_1"], #formula_1'),
  });

  const lockCalculatorUnitPrice = (calculator, unitPrice) => {
    if (!calculator || !Number.isFinite(unitPrice)) return;

    const pin = () => {
      const { productPriceInput, unitPriceInput, totalFormulaInput } = getCalculatorPriceInputs(calculator);
      if (unitPriceInput && totalFormulaInput) {
        setNativeValue(totalFormulaInput, 'formula_0*unit_price');
      }
      setNativeValue(productPriceInput, unitPrice);
      setNativeValue(unitPriceInput, unitPrice);
      hideCalculatorField(unitPriceInput);
    };

    const existingLock = priceLocks.get(calculator);
    if (existingLock) {
      existingLock.unitPrice = unitPrice;
      pin();
      return;
    }

    const lock = {
      unitPrice,
      scheduled: false,
      observer: null,
      repin() {
        if (lock.scheduled) return;
        lock.scheduled = true;
        window.requestAnimationFrame(() => {
          lock.scheduled = false;
          pin();
        });
      },
    };

    calculator.addEventListener('input', lock.repin, true);
    calculator.addEventListener('change', lock.repin, true);
    lock.observer = new MutationObserver(lock.repin);
    lock.observer.observe(calculator, {
      attributes: true,
      attributeFilter: ['value'],
      childList: true,
      subtree: true,
    });

    priceLocks.set(calculator, lock);
    pin();
  };

  const setThemeBuyButtonsHidden = (component, hidden) => {
    const details = component?.closest('.product-details');
    const buyButtons = details?.querySelector('.buy-buttons-block');
    if (!buyButtons) return;

    if (hidden) {
      if (!Object.hasOwn(buyButtons.dataset, 'novvaPcWasHidden')) {
        buyButtons.dataset.novvaPcWasHidden = String(buyButtons.hidden);
      }
      buyButtons.hidden = true;
    } else if (Object.hasOwn(buyButtons.dataset, 'novvaPcWasHidden')) {
      buyButtons.hidden = buyButtons.dataset.novvaPcWasHidden === 'true';
      delete buyButtons.dataset.novvaPcWasHidden;
    }
  };

  const setCalculatorHidden = (calculator, hidden) => {
    if (!calculator) return;

    const appWrapper = calculator.closest('.shopify-app-block') || calculator.parentElement;
    [calculator, appWrapper].forEach((element) => {
      if (!element) return;
      element.hidden = Boolean(hidden);
      element.style.display = hidden ? 'none' : '';
      element.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    });
  };

  const setPcAvailabilityStatus = (component, state) => {
    const status = component?.querySelector('[data-pricing-status]');
    if (!status) return;

    if (!status.dataset.novvaOriginalHtml) {
      status.dataset.novvaOriginalHtml = status.innerHTML;
    }

    if (state === 'unavailable') {
      status.dataset.novvaPcStatus = 'unavailable';
      status.hidden = false;
      status.querySelector('p').textContent =
        'Bu ürün için özel ölçü siparişi henüz aktif değil. Sipariş için bizimle iletişime geçebilirsiniz.';
      return;
    }

    if (status.dataset.novvaPcStatus === 'unavailable') {
      status.innerHTML = status.dataset.novvaOriginalHtml;
      status.hidden = true;
      delete status.dataset.novvaPcStatus;
    }
  };

  const hasVisibleCalculatorAddButton = (calculator) =>
    Boolean(
      calculator &&
        [...calculator.querySelectorAll('button')].some((button) => {
          const text = button.textContent.trim().toLowerCase();
          const rect = button.getBoundingClientRect();
          return (
            !button.disabled &&
            rect.width > 0 &&
            rect.height > 0 &&
            (text === 'sepete ekle' || text === 'add to cart')
          );
        })
    );

  const localizeCalculator = (calculator) => {
    if (!calculator) return;

    calculator.querySelectorAll('button').forEach((button) => {
      if (button.textContent.trim().toLowerCase() === 'add to cart') {
        button.textContent = 'Sepete ekle';
      }
    });

    const resultTitle = [...calculator.querySelectorAll('td, th')]
      .find((cell) => cell.textContent.trim().toLowerCase() === 'result');
    if (resultTitle) resultTitle.textContent = 'Hesaplama';
  };

  const removeLegacyBridgeNote = (calculator) => {
    calculator?.querySelector('[data-novva-pc-bridge-note]')?.remove();
  };

  const upsertCalculatorPropertyInputs = (calculator, quote) => {
    const properties = quoteProperties(quote);
    if (!calculator || !properties) return;

    const host = calculator.querySelector('form') || calculator.closest('form') || calculator;
    Object.entries(properties).forEach(([name, value]) => {
      const inputName = `properties[${name}]`;
      let input = host.querySelector(`input[data-novva-pc-property="${CSS.escape(name)}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        input.dataset.novvaPcProperty = name;
        host.append(input);
      }
      input.value = value;
      input.disabled = false;
    });
  };

  const addPropertiesToFormData = (formData, quote) => {
    const properties = quoteProperties(quote);
    if (!properties) return formData;

    Object.entries(properties).forEach(([name, value]) => {
      formData.set(`properties[${name}]`, value);
    });
    Object.entries(appPropertyAliases(quote)).forEach(([name, value]) => {
      formData.set(`properties[${name}]`, value);
    });
    return formData;
  };

  const applyQuoteFieldsToFormData = (formData, quote) => {
    if (!quote || quote.status !== 'ready') return formData;

    Object.entries(quoteCalculatorFields(quote)).forEach(([name, value]) => {
      formData.set(name, value);
    });
    addPropertiesToFormData(formData, quote);
    return formData;
  };

  const applyQuoteFieldsToObject = (payload, quote) => {
    if (!payload || typeof payload !== 'object' || !quote || quote.status !== 'ready') return payload;

    const calculatorFields = quoteCalculatorFields(quote);
    const aliases = appPropertyAliases(quote);
    const properties = quoteProperties(quote);

    const visit = (value) => {
      if (!value || typeof value !== 'object') return;

      if (Array.isArray(value)) {
        value.forEach(visit);
        return;
      }

      Object.keys(value).forEach((key) => {
        if (Object.hasOwn(calculatorFields, key)) {
          value[key] = calculatorFields[key];
          return;
        }

        const propertyMatch = key.match(/^properties\[(.+)\]$/);
        if (propertyMatch) {
          const propertyName = propertyMatch[1];
          if (Object.hasOwn(properties, propertyName)) value[key] = properties[propertyName];
          if (Object.hasOwn(aliases, propertyName)) value[key] = aliases[propertyName];
          return;
        }

        visit(value[key]);
      });

      Object.entries(calculatorFields).forEach(([key, nextValue]) => {
        if (Object.hasOwn(value, key)) value[key] = nextValue;
      });

      if (value.properties && typeof value.properties === 'object' && !Array.isArray(value.properties)) {
        value.properties = { ...value.properties, ...aliases, ...properties };
      }
    };

    visit(payload);

    Object.assign(payload, calculatorFields);
    payload.properties = { ...(payload.properties || {}), ...aliases, ...properties };
    return payload;
  };

  const addPropertiesToBody = (body, quote, headers) => {
    if (!quote || quote.status !== 'ready') return body;

    if (body instanceof FormData) return applyQuoteFieldsToFormData(body, quote);
    if (body instanceof URLSearchParams) {
      const params = new URLSearchParams(body);
      applyQuoteFieldsToFormData(params, quote);
      return params;
    }
    if (typeof body !== 'string') return body;

    const contentType =
      headers?.get?.('content-type') ||
      headers?.['content-type'] ||
      headers?.['Content-Type'] ||
      '';

    if (contentType.includes('application/json') || body.trim().startsWith('{')) {
      try {
        const payload = JSON.parse(body);
        applyQuoteFieldsToObject(payload, quote);
        return JSON.stringify(payload);
      } catch (_error) {
        return body;
      }
    }

    try {
      const params = new URLSearchParams(body);
      applyQuoteFieldsToFormData(params, quote);
      return params.toString();
    } catch (_error) {
      return body;
    }
  };

  /*
   * PC Price Calculator needs its own field aliases while it creates the
   * correctly priced variant. Those aliases must not become customer-facing
   * Shopify line-item properties, otherwise Checkout repeats the same size
   * information under several app/internal labels. Keep the app request
   * untouched and reduce only the final /cart/add payload to Novva's canonical
   * property set.
   */
  const cleanCartFormProperties = (formData, quote) => {
    [...formData.keys()].forEach((key) => {
      const propertyMatch = String(key).match(/^properties\[(.*)\]$/i);
      if (propertyMatch && isRedundantCartProperty(propertyMatch[1])) formData.delete(key);
    });

    Object.entries(quoteProperties(quote)).forEach(([name, value]) => {
      formData.set(`properties[${name}]`, value);
    });
    return formData;
  };

  const cleanCartObjectProperties = (payload, quote) => {
    if (!payload || typeof payload !== 'object') return payload;

    const visit = (value) => {
      if (!value || typeof value !== 'object') return;
      if (Array.isArray(value)) {
        value.forEach(visit);
        return;
      }

      Object.keys(value).forEach((key) => {
        const propertyMatch = key.match(/^properties\[(.*)\]$/i);
        if (propertyMatch && isRedundantCartProperty(propertyMatch[1])) {
          delete value[key];
          return;
        }

        if (key === 'properties') {
          value.properties = cleanCartProperties(value.properties, quote);
          return;
        }

        visit(value[key]);
      });
    };

    visit(payload);

    if (Array.isArray(payload.items)) {
      payload.items.forEach((item) => {
        if (item && typeof item === 'object') item.properties = cleanCartProperties(item.properties, quote);
      });
    } else {
      payload.properties = cleanCartProperties(payload.properties, quote);
    }

    return payload;
  };

  const cleanCartBody = (body, quote, headers) => {
    if (!quote || quote.status !== 'ready') return body;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      return cleanCartFormProperties(body, quote);
    }
    if (typeof body !== 'string') return body;

    const contentType =
      headers?.get?.('content-type') ||
      headers?.['content-type'] ||
      headers?.['Content-Type'] ||
      '';

    if (contentType.includes('application/json') || body.trim().startsWith('{')) {
      try {
        return JSON.stringify(cleanCartObjectProperties(JSON.parse(body), quote));
      } catch (_error) {
        return body;
      }
    }

    try {
      return cleanCartFormProperties(new URLSearchParams(body), quote).toString();
    } catch (_error) {
      return body;
    }
  };

  const preparePriceMutationBody = (url, body, quote, headers) => {
    const enrichedBody = addPropertiesToBody(body, quote, headers);
    return isCartAddUrl(url) ? cleanCartBody(enrichedBody, quote, headers) : enrichedBody;
  };

  const isCartAddUrl = (url) => String(url || '').includes('/cart/add');
  const isAddifyCreateVariantUrl = (url) =>
    String(url || '').includes('price-by-formula.addify-app.com/api/create-variant');
  const isPriceMutationUrl = (url) => isCartAddUrl(url) || isAddifyCreateVariantUrl(url);

  const installCartRequestBridge = () => {
    if (window.__novvaPcCartBridgeInstalled) return;
    window.__novvaPcCartBridgeInstalled = true;

    const nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = (input, init = {}) => {
        const url = typeof input === 'string' ? input : input?.url;
        if (document.body.classList.contains(CUSTOM_ACTIVE_CLASS) && isPriceMutationUrl(url) && lastReadyQuote) {
          init = {
            ...init,
            body: preparePriceMutationBody(url, init.body, lastReadyQuote, init.headers),
          };
        }
        return nativeFetch(input, init);
      };
    }

    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
      this.__novvaPcMethod = method;
      this.__novvaPcUrl = url;
      return nativeOpen.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function send(body) {
      if (
        document.body.classList.contains(CUSTOM_ACTIVE_CLASS) &&
        String(this.__novvaPcMethod || '').toUpperCase() === 'POST' &&
        isPriceMutationUrl(this.__novvaPcUrl) &&
        lastReadyQuote
      ) {
        body = preparePriceMutationBody(this.__novvaPcUrl, body, lastReadyQuote);
      }
      return nativeSend.call(this, body);
    };
  };

  const syncCalculator = (component, quote) => {
    const calculator = findCalculator(component);
    const isReadyCustom =
      component?.dataset.pricingReady === 'true' &&
      quote?.status === 'ready' &&
      Number.isInteger(quote.billableWidthCm) &&
      Number.isInteger(quote.billableLengthCm);

    const hasCalculator = Boolean(isReadyCustom && calculator);

    document.body.classList.toggle(CUSTOM_ACTIVE_CLASS, hasCalculator);
    setThemeBuyButtonsHidden(component, hasCalculator);

    if (hasCalculator) {
      lastReadyQuote = quote;
      lastReadyComponent = component;
    } else {
      lastReadyQuote = null;
      lastReadyComponent = null;
    }

    if (isReadyCustom && !calculator) {
      setPcAvailabilityStatus(component, 'unavailable');
      return;
    }

    if (!calculator) {
      setPcAvailabilityStatus(component, 'ready');
      return;
    }
    localizeCalculator(calculator);

    if (!isReadyCustom) {
      calculator.removeAttribute('data-novva-pc-ready');
      setCalculatorHidden(calculator, true);
      setPcAvailabilityStatus(component, 'ready');
      return;
    }

    setCalculatorHidden(calculator, false);
    calculator.dataset.novvaPcReady = 'true';

    const widthInput = calculator.querySelector('input[name="width"], #width');
    const lengthInput = calculator.querySelector('input[name="length"], #length');
    const { productPriceInput, unitPriceInput, totalFormulaInput } = getCalculatorPriceInputs(calculator);
    const unitPrice = quote.unitPriceCents / 100;

    /*
     * PC Price Calculator evaluates formulas as its fields change. Keep the
     * app pinned to Novva's m² price before touching the measurement inputs.
     *
     * The app's built-in `product_price` token can keep using the selected or
     * previously generated variant price internally even after its hidden DOM
     * input is changed. When the hidden unit_price field exists, override the
     * total formula to use that explicit Novva value instead.
     */
    if (unitPriceInput && totalFormulaInput) {
      setNativeValue(totalFormulaInput, 'formula_0*unit_price');
    }
    setNativeValue(productPriceInput, unitPrice);
    setNativeValue(unitPriceInput, unitPrice);
    setNativeValue(widthInput, quote.billableWidthCm);
    setNativeValue(lengthInput, quote.billableLengthCm);
    pulseCalculatorField(totalFormulaInput);
    pulseCalculatorField(productPriceInput);
    pulseCalculatorField(unitPriceInput);
    pulseCalculatorField(widthInput);
    pulseCalculatorField(lengthInput);
    lockCalculatorUnitPrice(calculator, unitPrice);
    hideCalculatorField(unitPriceInput);
    /*
     * The customer-facing quote is rendered by novva-size-options. Keep the
     * calculator focused on its single purchase action and remove the older
     * duplicate summary if it was rendered before this bridge version loaded.
     */
    removeLegacyBridgeNote(calculator);
    upsertCalculatorPropertyInputs(calculator, quote);
    setPcAvailabilityStatus(component, hasVisibleCalculatorAddButton(calculator) ? 'ready' : 'unavailable');
  };

  const scheduleCalculatorResync = (component, quote) => {
    pendingResyncTimers.forEach((timer) => window.clearTimeout(timer));
    pendingResyncTimers = [];

    if (!component || quote?.status !== 'ready') return;

    [250, 750, 1500].forEach((delay) => {
      pendingResyncTimers.push(
        window.setTimeout(() => {
          if (lastReadyQuote === quote && lastReadyComponent === component) {
            syncCalculator(component, quote);
          }
        }, delay)
      );
    });
  };

  const syncFromCurrentState = () => {
    const component = document.querySelector(SIZE_SELECTOR);
    if (!component) {
      document.body.classList.remove(CUSTOM_ACTIVE_CLASS);
      return;
    }

    const mode = component.querySelector('[data-size-property="type"]')?.value;
    if (mode !== 'Özel ölçü') {
      syncCalculator(component, { status: 'pending', reason: 'standard_mode' });
    }
  };

  document.addEventListener('novva:price-quote', (event) => {
    const component = event.target?.closest?.(SIZE_SELECTOR) || document.querySelector(SIZE_SELECTOR);
    syncCalculator(component, event.detail);
    scheduleCalculatorResync(component, event.detail);
  });

  const initialize = () => {
    document.body.classList.add('novva-pc-bridge-loaded');
    installCartRequestBridge();
    document.querySelectorAll(CALCULATOR_SELECTOR).forEach((calculator) => {
      localizeCalculator(calculator);
      setCalculatorHidden(calculator, true);
    });
    syncFromCurrentState();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
